import React, { useContext } from 'react'

// @mui material components
import { Autocomplete, Checkbox, Icon, IconButton } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { useMaterialUIController } from 'context/materialUI'
import {
	DBActionType,
	DBNewValue,
	FeedbackCategory,
	FeedbackConfiguration,
	FeedbackOutcome,
	FeedbackTypes,
} from 'context/materialUI/types'
import { SeverityTypes } from 'context/materialUI/types'
import { toTitleCase } from 'utils/string'

import { FormContext } from '../../constants'

interface Props {
	name: FeedbackTypes
	memberType: string
}

function FeedbackMemberOptions({ name, memberType }: Props): JSX.Element {
	const formContext = useContext(FormContext)
	const [controller] = useMaterialUIController()
	const { setLocalForm, setFormEdited, localForm } = formContext

	const severityTimelines = controller.configuration?.organisation.severityTimelines
	const severityOptions = severityTimelines.map(item => {
		return item.severity
	})

	const isPartOfMemberAndFeedbackType = (item: FeedbackCategory | FeedbackOutcome) =>
		item.memberType === memberType && item.feedbackType === name

	const handleFormUpdate = (
		field: keyof FeedbackConfiguration,
		value: FeedbackOutcome[] | FeedbackCategory[]
	) => {
		setLocalForm(prev => ({
			...prev,
			[field]: value,
		}))
		setFormEdited(true)
	}

	return (
		<Card>
			<MDBox p={2}>
				<Grid container spacing={3} alignItems="center">
					<Grid item xs={12}>
						<MDBox display={'flex'} flexDirection={'column'} gap={2}>
							<MDTypography variant="h4" fontWeight="medium">
								{toTitleCase(name)}
							</MDTypography>
							<MDTypography variant="button" fontWeight="regular" color="text">
								<i>
									Please create all the different {name.toLowerCase()} categories that you would
									like to track. This will be used to create the {name.toLowerCase()} categories for
									the feedback.
								</i>
							</MDTypography>
						</MDBox>
					</Grid>

					<Grid container spacing={2} mt={1} ml={2} alignItems="flex-start">
						<Grid item xs={12}>
							<MDBox display={'flex'} mt={2} flexDirection={'column'}>
								<MDTypography variant="h5" fontWeight="medium">
									{memberType} Categories
								</MDTypography>
							</MDBox>
						</Grid>
						<Grid item xs={12}>
							<MDBox display={'flex'} mt={1} flexDirection={'column'}>
								{localForm?.categories?.map((item, index) => {
									if (isPartOfMemberAndFeedbackType(item)) {
										return (
											<MDBox
												key={index}
												display={'flex'}
												flexDirection={'row'}
												gap={4}
												alignItems={'center'}
												justifyContent={'space-between'}
												mr={2}
												my={2}
											>
												<MDInput
													value={item.newValue?.find(nv => nv.field === 'name')?.value ?? item.name}
													variant="outlined"
													label="Category"
													sx={{ width: '15rem' }}
													InputLabelProps={{ shrink: true }}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														const userInput = e.target.value
														const updatedCategories = localForm?.categories.map((entry, i) => {
															if (i !== index) return entry

															const isUpdate = entry.dbAction !== 'CREATE'
															const existingNewValue = entry.newValue ?? []

															let newValue: DBNewValue[] | undefined = undefined

															if (isUpdate) {
																const otherFields = existingNewValue.filter(
																	nv => nv.field !== 'name'
																)
																newValue = [...otherFields, { field: 'name', value: userInput }]
															}

															const data: FeedbackCategory = {
																...entry,
																dbAction: isUpdate ? 'UPDATE' : 'CREATE',
																name: isUpdate ? entry.name : userInput,
																newValue,
															}
															return data
														})
														handleFormUpdate('categories', updatedCategories)
													}}
												/>
												<Autocomplete
													disableClearable
													value={
														item.newValue?.find(nv => nv.field === 'severity')?.value ??
														item.severity
													}
													options={severityOptions}
													onChange={(_, newAutocompleteValue) => {
														const updatedCategories = localForm?.categories.map((entry, i) => {
															const itemIndex = localForm.categories.indexOf(item)
															if (i !== itemIndex) return entry

															const isUpdate = entry.dbAction !== 'CREATE'
															const existingNewValue = entry.newValue ?? []

															let newValue: DBNewValue[] | undefined = undefined

															if (isUpdate) {
																const otherFields = existingNewValue.filter(
																	nv => nv.field !== 'severity'
																)
																newValue = [
																	...otherFields,
																	{ field: 'severity', value: newAutocompleteValue },
																]
															}

															const data: FeedbackCategory = {
																...entry,
																dbAction: isUpdate ? 'UPDATE' : 'CREATE',
																severity: isUpdate
																	? entry.severity
																	: (newAutocompleteValue as SeverityTypes),
																newValue,
															}
															return data
														})
														handleFormUpdate('categories', updatedCategories)
													}}
													size="medium"
													sx={{ width: '12rem' }}
													renderInput={params => <MDInput {...params} />}
												/>
												<MDBox
													display={'flex'}
													width={'10rem'}
													flexDirection={'row'}
													alignItems={'center'}
													justifyContent={'flex-end'}
												>
													{item.dbAction === 'DELETE' && (
														<MDTypography variant={'caption'} color="error" sx={{ mr: 1 }}>
															<i>Marked for deletion</i>
														</MDTypography>
													)}
													<IconButton
														size="medium"
														aria-label="delete"
														color="error"
														disabled={item.dbAction === 'DELETE'}
														onClick={() => {
															const index = localForm?.categories.indexOf(item)
															const temp = [...localForm.categories] // Create a shallow copy

															if (item.dbAction === 'CREATE') {
																temp.splice(index, 1) // Remove the item
															} else {
																temp[index] = { ...item, dbAction: 'DELETE', newValue: undefined } //mark it for deletion
															}
															handleFormUpdate('categories', temp)
														}}
													>
														<Icon fontSize="medium">delete_outline</Icon>
													</IconButton>
												</MDBox>
											</MDBox>
										)
									} else {
										return null
									}
								})}
								<MDButton
									color="primary"
									variant="outlined"
									sx={{ width: '20rem' }}
									onClick={() => {
										handleFormUpdate('categories', [
											...(localForm?.categories || []),
											{
												id: '-1',
												memberType: memberType,
												feedbackType: name,
												status: 'ACTIVE',
												name: '',
												severity: 'Medium',
												dbAction: 'CREATE',
											},
										])
									}}
								>
									+ Add New {name} Category
								</MDButton>
							</MDBox>
						</Grid>
						<Grid item xs={12}>
							<MDBox display={'flex'} mt={2} flexDirection={'column'}>
								<MDTypography variant="h5" fontWeight="medium">
									{memberType} Outcomes
								</MDTypography>
							</MDBox>
						</Grid>
						<Grid item xs={12}>
							<MDBox display={'flex'} flexDirection={'column'}>
								{localForm?.outcomes?.map((item, index) => {
									if (isPartOfMemberAndFeedbackType(item)) {
										return (
											<MDBox
												key={index}
												display={'flex'}
												flexDirection={'row'}
												gap={4}
												alignItems={'center'}
												justifyContent={'space-between'}
												mr={2}
												my={2}
											>
												<MDInput
													value={item.newValue?.find(nv => nv.field === 'name')?.value ?? item.name}
													variant="outlined"
													label="Outcome"
													sx={{ width: '20rem' }}
													InputLabelProps={{ shrink: true }}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														const userInput = e.target.value
														const updatedMemberOutcomes = localForm?.outcomes.map((entry, i) => {
															if (i !== index) return entry
															const isUpdate = entry.dbAction !== 'CREATE'

															return {
																...entry,
																dbAction: isUpdate
																	? ('UPDATE' as DBActionType)
																	: ('CREATE' as DBActionType),
																// Keep original outcome untouched if it's an update
																newValue: isUpdate
																	? [
																			{
																				field: 'name',
																				value: userInput,
																			},
																		]
																	: undefined,
																// Only set outcome directly for new items (CREATE)
																name: isUpdate ? entry.name : userInput,
															}
														})
														handleFormUpdate('outcomes', updatedMemberOutcomes)
													}}
												/>

												<MDBox
													display={'flex'}
													flexDirection={'column'}
													alignItems={'center'}
													justifyContent={'center'}
												>
													{index ===
														localForm.outcomes.indexOf(
															localForm?.outcomes.filter(isPartOfMemberAndFeedbackType)[0]
														) && (
														<MDTypography variant="body2" fontWeight={'regular'}>
															Has Monetary Value?
														</MDTypography>
													)}
													<Checkbox
														checked={
															item.newValue?.find(nv => nv.field === 'has_monetary_value')?.value ??
															item.has_monetary_value
														}
														onChange={() => {
															const updatedOutcomes = localForm?.outcomes?.map((entry, i) => {
																if (i !== index) return entry
																const isUpdate = entry.dbAction !== 'CREATE'
																return {
																	...entry,
																	dbAction: isUpdate
																		? ('UPDATE' as DBActionType)
																		: ('CREATE' as DBActionType),
																	// Keep original outcome untouched if it's an update
																	newValue: isUpdate
																		? [
																				{
																					field: 'has_monetary_value',
																					value: !(
																						item.newValue?.find(
																							nv => nv.field === 'has_monetary_value'
																						)?.value ?? item.has_monetary_value
																					),
																				},
																			]
																		: undefined,
																	// Only set outcome directly for new items (CREATE)
																	has_monetary_value: isUpdate
																		? entry.has_monetary_value
																		: !item.has_monetary_value,
																}
															})
															handleFormUpdate('outcomes', updatedOutcomes)
														}}
													/>
												</MDBox>
												<MDBox
													display={'flex'}
													width={'10rem'}
													flexDirection={'row'}
													alignItems={'center'}
													justifyContent={'flex-end'}
												>
													{item.dbAction === 'DELETE' && (
														<MDTypography variant={'caption'} color="error" sx={{ mr: 1 }}>
															<i>Marked for deletion</i>
														</MDTypography>
													)}
													<IconButton
														size="medium"
														aria-label="delete"
														color="error"
														disabled={item.dbAction === 'DELETE'}
														onClick={() => {
															const index = localForm.outcomes.indexOf(item)
															const temp = [...localForm.outcomes] // Create a shallow copy

															if (item.dbAction === 'CREATE') {
																temp.splice(index, 1) // Remove the item
															} else {
																temp[index] = { ...item, dbAction: 'DELETE', newValue: undefined } //mark it for deletion
															}
															handleFormUpdate('outcomes', temp)
														}}
													>
														<Icon fontSize="medium">delete_outline</Icon>
													</IconButton>
												</MDBox>
											</MDBox>
										)
									} else {
										return null
									}
								})}
								<MDButton
									color="primary"
									variant="outlined"
									sx={{ width: '20rem' }}
									onClick={() => {
										handleFormUpdate('outcomes', [
											...(localForm?.outcomes || []),
											{
												id: -1,
												memberType: memberType,
												feedbackType: name,
												status: 'ACTIVE',
												has_monetary_value: false,
												name: '',
												dbAction: 'CREATE',
											},
										])
									}}
								>
									+ Add New {name} Outcome
								</MDButton>
							</MDBox>
						</Grid>
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default FeedbackMemberOptions
